import { Link, useLocation } from "@tanstack/react-router";
import {
  ActivityIcon,
  BarChart3Icon,
  BellIcon,
  BrainCircuitIcon,
  BriefcaseBusinessIcon,
  CalendarClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  HashIcon,
  MessageSquareIcon,
  UsersRoundIcon,
} from "lucide-react";
import { Fragment, useEffect, useState, type ReactElement, type ReactNode } from "react";

import { useDesktopPraxaAutomations } from "../../state/desktopPraxaAutomations";
import { useDesktopPraxaOrganizationPeople } from "../../state/desktopPraxaOrganizationPeople";
import { useDesktopPraxaNotifications } from "../../state/desktopPraxaNotifications";
import { useDesktopPraxaResources } from "../../state/desktopPraxaResources";
import { SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import {
  PraxaCustomizeNavigationIcon,
  PraxaOverviewNavigationIcon,
} from "./PraxaWorkspaceNavigationIcons";

const DEFAULT_VISIBLE_PRODUCT_NAVIGATION_ITEMS = 5;

interface ProductNavigationItem {
  readonly active: boolean;
  readonly content: ReactElement;
  readonly key: string;
}

function ProductNavigationMenuItem({
  active,
  ariaLabel,
  badge,
  icon,
  label,
  link,
}: {
  readonly active: boolean;
  readonly ariaLabel: string;
  readonly badge?: string | undefined;
  readonly icon: ReactNode;
  readonly label: string;
  readonly link: ReactElement;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        aria-label={ariaLabel}
        className={`min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-sidebar-muted-foreground/80 hover:bg-sidebar-row-hover hover:text-sidebar-foreground${
          badge === undefined ? "" : " pr-8"
        }`}
        isActive={active}
        render={link}
        size="sm"
      >
        {icon}
        <span>{label}</span>
      </SidebarMenuButton>
      {badge === undefined ? null : <SidebarMenuBadge aria-hidden>{badge}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
}

export function resolvePraxaRouteWorkspaceId(pathname: string): string | null {
  const encodedWorkspaceId = /^\/w\/([^/]+)/u.exec(pathname)?.[1];
  if (!encodedWorkspaceId) return null;
  try {
    return decodeURIComponent(encodedWorkspaceId);
  } catch {
    return null;
  }
}

export function PraxaHistoryNavigation() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { state: resources } = useDesktopPraxaResources();
  const { state: automations } = useDesktopPraxaAutomations();
  const { state: people } = useDesktopPraxaOrganizationPeople();
  const { state: notifications } = useDesktopPraxaNotifications();
  const routeWorkspaceId = resolvePraxaRouteWorkspaceId(pathname);
  const fallbackWorkspaceId = routeWorkspaceId ?? "personal";
  const resourceWorkspaceId = resources?.workspaceId ?? fallbackWorkspaceId;
  const automationWorkspaceId = automations?.workspaceId ?? fallbackWorkspaceId;
  const customizeWorkspaceId = routeWorkspaceId ?? resourceWorkspaceId;
  const peopleWorkspaceId = people?.workspaceId ?? null;
  const notificationWorkspaceId = notifications?.workspaceId ?? null;

  const personal =
    resources?.workspaceKind === "personal" ||
    (resources?.workspaceKind == null && fallbackWorkspaceId === "personal");
  const label = personal ? "Conversations" : "Channels";
  const historyPath =
    resourceWorkspaceId === null ? null : `/w/${encodeURIComponent(resourceWorkspaceId)}`;
  const overviewPath =
    resourceWorkspaceId === null ? null : `/w/${encodeURIComponent(resourceWorkspaceId)}/overview`;
  const productionPath =
    resourceWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(resourceWorkspaceId)}/production`;
  const connectedWorkPath =
    resourceWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(resourceWorkspaceId)}/connected-work`;
  const usagePath =
    resourceWorkspaceId === null ? null : `/w/${encodeURIComponent(resourceWorkspaceId)}/usage`;
  const memoryPath =
    resourceWorkspaceId === null ? null : `/w/${encodeURIComponent(resourceWorkspaceId)}/memory`;
  const agentPath =
    customizeWorkspaceId === null ? null : `/w/${encodeURIComponent(customizeWorkspaceId)}/agents`;
  const skillPath =
    customizeWorkspaceId === null ? null : `/w/${encodeURIComponent(customizeWorkspaceId)}/skills`;
  const automationPath =
    automationWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(automationWorkspaceId)}/automations`;
  const schedulePath =
    automationWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(automationWorkspaceId)}/schedule`;
  const workflowPath =
    customizeWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(customizeWorkspaceId)}/workflows`;
  const connectorPath =
    customizeWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(customizeWorkspaceId)}/connectors`;
  const customizePath =
    customizeWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(customizeWorkspaceId)}/customize`;
  const peoplePath =
    peopleWorkspaceId === null ? null : `/w/${encodeURIComponent(peopleWorkspaceId)}/people`;
  const notificationPath =
    notificationWorkspaceId === null
      ? null
      : `/w/${encodeURIComponent(notificationWorkspaceId)}/notifications`;
  const historyActive =
    historyPath !== null &&
    (pathname === historyPath ||
      (pathname.startsWith(`${historyPath}/`) &&
        (overviewPath === null || !pathname.startsWith(overviewPath)) &&
        (productionPath === null || !pathname.startsWith(productionPath)) &&
        (connectedWorkPath === null || !pathname.startsWith(connectedWorkPath)) &&
        (usagePath === null || !pathname.startsWith(usagePath)) &&
        (memoryPath === null || !pathname.startsWith(memoryPath)) &&
        (customizePath === null || !pathname.startsWith(customizePath)) &&
        (agentPath === null || !pathname.startsWith(agentPath)) &&
        (skillPath === null || !pathname.startsWith(skillPath)) &&
        (workflowPath === null || !pathname.startsWith(workflowPath)) &&
        (automationPath === null || !pathname.startsWith(automationPath)) &&
        (schedulePath === null || !pathname.startsWith(schedulePath)) &&
        (peoplePath === null || !pathname.startsWith(peoplePath)) &&
        (notificationPath === null || !pathname.startsWith(notificationPath)) &&
        (connectorPath === null || !pathname.startsWith(connectorPath))));
  const overviewActive = overviewPath !== null && pathname === overviewPath;
  const productionActive =
    productionPath !== null &&
    (pathname === productionPath || pathname.startsWith(`${productionPath}/`));
  const connectedWorkActive =
    connectedWorkPath !== null &&
    (pathname === connectedWorkPath || pathname.startsWith(`${connectedWorkPath}/`));
  const usageActive = usagePath !== null && pathname === usagePath;
  const memoryActive = memoryPath !== null && pathname === memoryPath;
  const scheduleActive =
    schedulePath !== null && (pathname === schedulePath || pathname.startsWith(`${schedulePath}/`));
  const customizeActive =
    (customizePath !== null &&
      (pathname === customizePath || pathname.startsWith(`${customizePath}/`))) ||
    (agentPath !== null && (pathname === agentPath || pathname.startsWith(`${agentPath}/`))) ||
    (skillPath !== null && (pathname === skillPath || pathname.startsWith(`${skillPath}/`))) ||
    (automationPath !== null &&
      (pathname === automationPath || pathname.startsWith(`${automationPath}/`))) ||
    (workflowPath !== null &&
      (pathname === workflowPath || pathname.startsWith(`${workflowPath}/`))) ||
    (connectorPath !== null &&
      (pathname === connectorPath || pathname.startsWith(`${connectorPath}/`)));
  const peopleActive =
    peoplePath !== null && (pathname === peoplePath || pathname.startsWith(`${peoplePath}/`));
  const notificationsActive =
    notificationPath !== null &&
    (pathname === notificationPath || pathname.startsWith(`${notificationPath}/`));

  const navigationItems = [
    resourceWorkspaceId === null
      ? null
      : {
          key: "overview",
          active: overviewActive,
          content: (
            <ProductNavigationMenuItem
              active={overviewActive}
              ariaLabel="Open Praxa overview"
              icon={
                <PraxaOverviewNavigationIcon
                  active={overviewActive}
                  className="size-4.5 shrink-0"
                />
              }
              label="Overview"
              link={
                <Link params={{ workspaceId: resourceWorkspaceId }} to="/w/$workspaceId/overview" />
              }
            />
          ),
        },
    productionPath === null
      ? null
      : {
          key: "production",
          active: productionActive,
          content: (
            <ProductNavigationMenuItem
              active={productionActive}
              ariaLabel="Open Praxa Production"
              icon={<ActivityIcon className="size-4.5 shrink-0" />}
              label="Production"
              link={
                <Link
                  params={{ workspaceId: resourceWorkspaceId }}
                  to="/w/$workspaceId/production"
                />
              }
            />
          ),
        },
    connectedWorkPath === null
      ? null
      : {
          key: "connected-work",
          active: connectedWorkActive,
          content: (
            <ProductNavigationMenuItem
              active={connectedWorkActive}
              ariaLabel="Open Praxa Work"
              icon={<BriefcaseBusinessIcon className="size-4.5 shrink-0" />}
              label="Work"
              link={
                <Link
                  params={{ workspaceId: resourceWorkspaceId }}
                  to="/w/$workspaceId/connected-work"
                />
              }
            />
          ),
        },
    resourceWorkspaceId === null
      ? null
      : {
          key: "history",
          active: historyActive,
          content: (
            <ProductNavigationMenuItem
              active={historyActive}
              ariaLabel={`Open Praxa ${label.toLocaleLowerCase()}`}
              icon={
                personal ? (
                  <MessageSquareIcon className="size-4.5 shrink-0" />
                ) : (
                  <HashIcon className="size-4.5 shrink-0" />
                )
              }
              label={label}
              link={<Link params={{ workspaceId: resourceWorkspaceId }} to="/w/$workspaceId" />}
            />
          ),
        },
    notificationWorkspaceId === null
      ? null
      : {
          key: "notifications",
          active: notificationsActive,
          content: (
            <ProductNavigationMenuItem
              active={notificationsActive}
              ariaLabel={`Open Praxa notifications${
                notifications && notifications.unreadCount > 0
                  ? `, ${notifications.unreadCount} unread`
                  : ""
              }`}
              badge={
                notifications && notifications.unreadCount > 0
                  ? notifications.unreadCount > 99
                    ? "99+"
                    : String(notifications.unreadCount)
                  : undefined
              }
              icon={<BellIcon className="size-4.5 shrink-0" />}
              label="Notifications"
              link={
                <Link
                  params={{ workspaceId: notificationWorkspaceId }}
                  to="/w/$workspaceId/notifications"
                />
              }
            />
          ),
        },
    peopleWorkspaceId === null
      ? null
      : {
          key: "people",
          active: peopleActive,
          content: (
            <ProductNavigationMenuItem
              active={peopleActive}
              ariaLabel="Open Praxa organization People"
              icon={<UsersRoundIcon className="size-4.5 shrink-0" />}
              label="People"
              link={
                <Link params={{ workspaceId: peopleWorkspaceId }} to="/w/$workspaceId/people" />
              }
            />
          ),
        },
    customizeWorkspaceId === null
      ? null
      : {
          key: "customize",
          active: customizeActive,
          content: (
            <ProductNavigationMenuItem
              active={customizeActive}
              ariaLabel="Open Customize"
              icon={<PraxaCustomizeNavigationIcon className="size-4.5 shrink-0" />}
              label="Customize"
              link={
                <Link
                  params={{ workspaceId: customizeWorkspaceId }}
                  search={{ tab: "connectors" }}
                  to="/w/$workspaceId/customize"
                />
              }
            />
          ),
        },
    resourceWorkspaceId === null
      ? null
      : {
          key: "usage",
          active: usageActive,
          content: (
            <ProductNavigationMenuItem
              active={usageActive}
              ariaLabel="Open Praxa usage"
              icon={<BarChart3Icon className="size-4.5 shrink-0" />}
              label="Usage"
              link={
                <Link params={{ workspaceId: resourceWorkspaceId }} to="/w/$workspaceId/usage" />
              }
            />
          ),
        },
    resourceWorkspaceId === null
      ? null
      : {
          key: "memory",
          active: memoryActive,
          content: (
            <ProductNavigationMenuItem
              active={memoryActive}
              ariaLabel="Open Praxa memory"
              icon={<BrainCircuitIcon className="size-4.5 shrink-0" />}
              label="Memory"
              link={
                <Link params={{ workspaceId: resourceWorkspaceId }} to="/w/$workspaceId/memory" />
              }
            />
          ),
        },
    automationWorkspaceId === null
      ? null
      : {
          key: "schedule",
          active: scheduleActive,
          content: (
            <ProductNavigationMenuItem
              active={scheduleActive}
              ariaLabel="Open Praxa schedule"
              icon={<CalendarClockIcon className="size-4.5 shrink-0" />}
              label="Schedule"
              link={
                <Link
                  params={{ workspaceId: automationWorkspaceId }}
                  to="/w/$workspaceId/schedule"
                />
              }
            />
          ),
        },
  ].filter((item): item is ProductNavigationItem => item !== null);
  const hiddenNavigationItems = navigationItems.slice(DEFAULT_VISIBLE_PRODUCT_NAVIGATION_ITEMS);
  const activeHiddenItemKey = hiddenNavigationItems.find((item) => item.active)?.key ?? null;
  const [expanded, setExpanded] = useState(activeHiddenItemKey !== null);

  useEffect(() => {
    if (activeHiddenItemKey !== null) {
      setExpanded(true);
    }
  }, [activeHiddenItemKey]);

  const visibleNavigationItems = expanded
    ? navigationItems
    : navigationItems.slice(0, DEFAULT_VISIBLE_PRODUCT_NAVIGATION_ITEMS);

  return (
    <SidebarMenu>
      {visibleNavigationItems.map((item) => (
        <Fragment key={item.key}>{item.content}</Fragment>
      ))}
      {hiddenNavigationItems.length > 0 ? (
        <SidebarMenuItem>
          <button
            aria-expanded={expanded}
            className="flex min-h-7 w-full cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 py-1 text-left text-xs font-medium text-sidebar-muted-foreground/80 outline-hidden hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setExpanded((current) => !current)}
            type="button"
          >
            {expanded ? (
              <ChevronUpIcon aria-hidden className="size-3.5 shrink-0" />
            ) : (
              <ChevronDownIcon aria-hidden className="size-3.5 shrink-0" />
            )}
            <span>{expanded ? "View less" : "View more"}</span>
          </button>
        </SidebarMenuItem>
      ) : null}
    </SidebarMenu>
  );
}
